import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { ChatContainer } from './ChatContainer.tsx';
import { useChatStore } from './chatStore.ts';

// Create a context for chatbot state
interface ChatbotContextType {
  isOpen: boolean;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

// Provider component with persistent state
export const ChatbotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state from localStorage for persistence across refreshes
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('wedify-chatbot-open');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  // Persist state to localStorage
  useEffect(() => {
    localStorage.setItem('wedify-chatbot-open', JSON.stringify(isOpen));
  }, [isOpen]);

  const toggleChat = useCallback(() => setIsOpen(prev => {
    const newState = !prev;
    localStorage.setItem('wedify-chatbot-open', JSON.stringify(newState));
    return newState;
  }), []);
  
  const openChat = useCallback(() => {
    setIsOpen(true);
    localStorage.setItem('wedify-chatbot-open', JSON.stringify(true));
  }, []);
  
  const closeChat = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem('wedify-chatbot-open', JSON.stringify(false));
  }, []);

  return (
    <ChatbotContext.Provider value={{ isOpen, toggleChat, openChat, closeChat }}>
      {children}
    </ChatbotContext.Provider>
  );
};

// Hook to use chatbot context
export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};

interface GlobalChatbotProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

// Singleton instance management
let chatbotInstanceRef: HTMLElement | null = null;
let isInitialized = false;

// Main GlobalChatbot component that uses context and Portal
export const GlobalChatbotWithContext: React.FC = () => {
  const { isOpen, toggleChat } = useChatbot();
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Only create one instance ever
    if (isInitialized) {
      console.log('🤖 GlobalChatbot already initialized, skipping...');
      setPortalContainer(chatbotInstanceRef);
      setIsReady(true);
      return;
    }

    // Create or get the persistent chatbot container
    let container = document.getElementById('wedify-chatbot-portal');
    if (!container) {
      container = document.createElement('div');
      container.id = 'wedify-chatbot-portal';
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.pointerEvents = 'none'; // Allow clicks to pass through
      container.style.zIndex = '9999';
      container.style.transition = 'opacity 0.2s ease-in-out';
      document.body.appendChild(container);
      
      console.log('🤖 Created persistent chatbot portal container');
    } else {
      console.log('🤖 Reusing existing chatbot portal container');
    }
    
    chatbotInstanceRef = container;
    isInitialized = true;
    setPortalContainer(container);
    setIsReady(true);

    // This cleanup never runs because we want the container to persist forever
    return () => {
      console.log('🤖 Chatbot component cleanup - but keeping portal alive');
    };
  }, []);

  // Only render once the portal is ready and we have a container
  if (!portalContainer || !isReady) {
    return null;
  }

  return createPortal(
    <GlobalChatbot isOpen={isOpen} onToggle={toggleChat} />,
    portalContainer
  );
};

export const GlobalChatbot: React.FC<GlobalChatbotProps> = ({ isOpen = false, onToggle = () => {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem('wedify-chatbot-position');
      if (saved) {
        const savedPos = JSON.parse(saved);
        if (savedPos.x >= 0 && savedPos.x <= window.innerWidth - 100 && 
            savedPos.y >= 0 && savedPos.y <= window.innerHeight - 120) {
          return savedPos;
        }
      }
    } catch {
      // Fallback to default position
    }
    return { x: window.innerWidth - 164, y: window.innerHeight - 120 };
  });
  
  const dragControls = useDragControls();
  const constraintsRef = useRef(null);

  // Get chat store data
  const { sendMessage, messages, loadConversationHistory, isHistoryLoaded, isHistoryLoading } = useChatStore();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);



  useEffect(() => {
    if (!isHistoryLoaded && !isHistoryLoading) {
      console.log('🚀 Loading conversation history in GlobalChatbot...');
      loadConversationHistory();
    }
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleResize = () => {
      checkMobile();
      setPosition(prev => {
        const newPosition = {
          x: Math.min(Math.max(prev.x, 20), window.innerWidth - 100),
          y: Math.min(Math.max(prev.y, 20), window.innerHeight - 120)
        };
        localStorage.setItem('wedify-chatbot-position', JSON.stringify(newPosition));
        return newPosition;
      });
    };

    checkMobile();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Drag constraints container */}
      <div ref={constraintsRef} className="fixed inset-0 pointer-events-none" />



      {/* Chat Interface */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Mobile backdrop */}
            {isMobile && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/20 z-[9997] pointer-events-auto"
                onClick={onToggle}
              />
            )}
            
            {/* Chat Panel */}
            <motion.div
              initial={{ 
                x: isMobile ? "-100%" : "-100%", 
                opacity: 0
              }}
              animate={{ 
                x: 0, 
                opacity: 1
              }}
              exit={{ 
                x: isMobile ? "-100%" : "-100%", 
                opacity: 0
              }}
              transition={{ 
                type: "spring",
                stiffness: 400,
                damping: 30,
                opacity: { duration: 0.2 }
              }}
              className={`fixed z-[9998] pointer-events-auto ${
                isMobile 
                  ? 'inset-0' 
                  : 'left-16 top-[72px] bottom-0 w-[420px]'
              }`}
            >
              <div className="h-full flex flex-col bg-white border-2 border-gray-300 rounded-lg shadow-xl">
                


                {/* Chat Content */}
                {(
                  <>
                    <div className="flex-1 overflow-hidden bg-gray-50">
                      <ChatContainer variant="sidebar" />
                    </div>
                    
                    {/* Colorful Close Button in Header */}
                    <div className="absolute top-3 right-3 z-10">
                      <button
                        onClick={onToggle}
                        className="p-2 bg-gradient-to-r from-pink-500 to-orange-400 text-white hover:from-pink-600 hover:to-orange-500 rounded-full transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                        title="Minimize Chat"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}; 